// ----------------------------------------------------------------------------
// Для управления модальными окнами
// ----------------------------------------------------------------------------


import createElement from "../create/createElement.js";

/**
 * Создает клон модального окна на основе тега <template>
 *
 * @param {string} [templateId='modal-template'] - id шаблона (тега <template>)
 * @param {string|HTMLElement|Node|null} [modalContent] - содержимое окна (текст, HTML-строка или DOM-узел)
 * @param {string|HTMLElement|Node|null} [modalFooter] - содержимое подвала (текст, HTML-строка или DOM-узел)
 * @param {boolean} closeButton - выводить или нет крестик сверху у окна
 *
 * @returns {DocumentFragment|undefined} Возвращает фрагмент документа с разметкой или undefined при ошибке
 */
export const modalWindowCreateFromTemplate = (templateId = 'modal-template', modalContent = null, modalFooter = null, closeButton = true) => {
    const modalTemplate = document.getElementById(templateId);

    if (!modalTemplate) {
        console.warn(`Модальное окно ${templateId} не найдено в разметке`);
        return;
    }

    const modalClone = modalTemplate.content.cloneNode(true);

    /**
     * Вспомогательная функция для вставки контента разного типа
     * @param {string} slotName - имя слота (атрибут data-slot)
     * @param {string|HTMLElement|Node|null} content - вставляемый контент
     */
    const insertContent = (slotName, content) => {
        if (!content) return;

        const slotPlace = modalClone.querySelector(`[data-modal-window-slot="${slotName}"]`);

        if (!slotPlace) {
            console.warn(`У модального окна ${templateId} не найдено поле ${slotName}`);
            return;
        }

        if (typeof content === 'string') {
            // Если это строка (простой текст или HTML-теги текстом)
            slotPlace.innerHTML = content;
        } else if (content instanceof Node) {
            // Если это DOM-узел (созданный через createElement или DocumentFragment)
            slotPlace.innerHTML = ''; // Очищаем слот от дефолтного контента, если он там был
            slotPlace.append(content);
        }
    };

    // если closeButton = false (крестик не нужен), то убираем его
    if (closeButton) {
        const closeButtonEl = modalClone.querySelector(`[data-modal-window="close"]`);
        const modalWrapper = modalClone.querySelector(`[data-modal-window="wrapper"]`);

        if (closeButtonEl && modalWrapper) {
            closeButtonEl.addEventListener('click', () => {
                modalWrapper.remove();
            });
        }
    } else {
        modalClone.querySelector('.modal-window__close')?.remove();
    }

    insertContent('modal-content', modalContent);
    insertContent('modal-footer', modalFooter);

    return modalClone;
};



/**
 * Создает кнопку закрытия окна для вставки в подвал мобильного окна
 *
 * @param {string[]} [classes=['button', 'button--m', 'button--black']] - массив классов стилей кнопки
 * @param {string} [text='Закрыть'] - текст на кнопке
 * @returns {HTMLElement} возвращает созданную кнопку, которую нужно вставить в нужное место, предварительно повесив обработчик
 */
export const createModalCloseButtonInFooter = (classes = ['button', 'button--m', 'button--black'], text = 'Закрыть') => {
    return createElement('button', {type: 'button', class: classes}, text);
}



/**
 * Навешивает слушатель на кнопку закрытия модального окна
 * @param {String|HTMLElement} closeButton - id кнопки или сам HTML-элемент кнопки
 * @param {String|HTMLElement} modalWindow - id модального окна или сам HTML-элемент окна
 */

export const modalWindowCloseManage = (closeButton, modalWindow) => {
    // Определяем, передана строка (id) или сам DOM-элемент
    const buttonElement = typeof closeButton === 'string'
        ? document.getElementById(closeButton)
        : closeButton;

    buttonElement?.addEventListener('click', () => {
        //closeButton.dataset.closeBound = 'true';
        // Такую же проверку делаем и для самого модального окна
        const modalElement = typeof modalWindow === 'string'
            ? document.getElementById(modalWindow)
            : modalWindow;

        if (modalElement) {
            modalElement.remove();
        }
    });
};



/**
 * Создает модальное окно без дополнительных условий.
 *
 * @param {string|HTMLElement|Node} content - Вставляемый контент (текст, HTML-строка или DOM-узел)
 * @param {string} [templateId='modal-template'] - ID template-шаблона модального окна
 * @param {string|HTMLElement|Node|null} [modalFooter=null] - Подвал модального окна (текст, HTML-строка или DOM-узел)
 * @param {boolean} [closeButton=true] - Выводить или нет крестик закрытия окна
 * @returns {void}
 */

export const createSimpleModal = (content, templateId = 'modal-template', modalFooter = null, closeButton = true) => {
    let modalContent;
    //проверяем, что пришло в качестве контента
    if (typeof content === 'string') {
        modalContent = `<p class="modal-window__p">${content}</p>`;
    } else if (content instanceof Node) {
        modalContent = content;
    } else {
        console.error('Передан неподдерживаемый формат данных', content);
        return;
    }

    // сделали клон модалки из template
    const modalFragment = modalWindowCreateFromTemplate(templateId, modalContent, modalFooter, closeButton);

    // показали окно
    if (modalFragment) {
        const modalWrapper = modalFragment.querySelector(`[data-modal-window="wrapper"]`);
        if (modalWrapper) {
            modalWrapper.style.display = 'flex';
        }
        document.body.append(modalFragment);
    }
}


/**
 * Обработчик события для закрытия модального окна.
 * Находит родительский элемент модального окна относительно кнопки и удаляет его из DOM.
 *
 * @param {Event} [event] - Объект события (клик).
 * @param {string} [wrapperSelector='[data-modal-window="wrapper"]'] - CSS-селектор обертки модального окна.
 */
export const closeModalWindow = (event, wrapperSelector = '[data-modal-window="wrapper"]') => {
    event?.preventDefault();
    let modal;

    if (event) {
        const clickButton = event.currentTarget;

        // Ищем модалку - ближайший родитель по переданному или дефолтному селектору
        modal = clickButton.closest(wrapperSelector);
    } else {
        modal = document.querySelector(wrapperSelector);
    }

    if (modal) {
        modal.remove();
    } else {
        console.error('Не найдено модальное окно для закрытия');
    }
};
